import React, { useEffect } from 'react';
import { IconAlert, IconX } from './icons';

/* ---------- Card ---------- */

export function Card({
  title,
  action,
  className = '',
  bodyClassName = '',
  children,
}: {
  title?: string;
  action?: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`card ${className}`}>
      {(title || action) && (
        <div className="flex items-center justify-between gap-3 border-b border-edge px-5 py-4">
          {title && <h2 className="text-sm font-semibold">{title}</h2>}
          {action}
        </div>
      )}
      <div className={`p-5 ${bodyClassName}`}>{children}</div>
    </div>
  );
}

/* ---------- Loading skeleton ---------- */

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded-xl bg-edge/60 ${className}`} />;
}

export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-3 p-5">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

/* ---------- Empty / error states ---------- */

export function EmptyState({ message, hint }: { message: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 py-12 text-center">
      <p className="text-sm font-medium text-sub">{message}</p>
      {hint && <p className="text-xs text-sub/70">{hint}</p>}
    </div>
  );
}

export function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-bad/40 bg-bad/10 px-4 py-3 text-sm text-bad">
      <span className="flex items-center gap-2">
        <IconAlert className="h-4 w-4 shrink-0" />
        {message}
      </span>
      {onRetry && (
        <button onClick={onRetry} className="shrink-0 font-semibold underline underline-offset-2">
          Retry
        </button>
      )}
    </div>
  );
}

export function Spinner({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/* ---------- Modal ---------- */

/** Dialog widths. Anything holding a table needs more than the default. */
const MODAL_WIDTHS = {
  md: 'max-w-md',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
} as const;

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  size = 'md',
  bodyClassName = 'px-5 py-4',
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: keyof typeof MODAL_WIDTHS;
  /** Override the body padding — a full-bleed table wants none. */
  bodyClassName?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        className={`card relative z-10 max-h-[90vh] w-full overflow-y-auto ${MODAL_WIDTHS[size]}`}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-edge bg-surface px-5 py-4">
          <h3 className="text-sm font-semibold">{title}</h3>
          <button onClick={onClose} className="btn-ghost !p-1.5" aria-label="Close">
            <IconX className="h-4 w-4" />
          </button>
        </div>
        <div className={bodyClassName}>{children}</div>
        {footer && <div className="flex justify-end gap-3 border-t border-edge px-5 py-4">{footer}</div>}
      </div>
    </div>
  );
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  danger = false,
  busy = false,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn-outline" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className={danger ? 'btn-danger' : 'btn-gold'} onClick={onConfirm} disabled={busy}>
            {busy && <Spinner />}
            {confirmLabel}
          </button>
        </>
      }
    >
      <div className="text-sm text-sub">{message}</div>
    </Modal>
  );
}

/* ---------- Load more ---------- */

/**
 * Append-the-next-page control, for lists that read top to bottom.
 *
 * Chosen over <Pagination> where the rows are a ranked feed rather than a
 * table you look things up in: numbered pages make you hold "which page was
 * that on" in your head, and these lists are scanned, not indexed. Renders the
 * running count either way, so the end of the list is never a surprise.
 */
export function LoadMore({
  shown,
  total,
  loading,
  onMore,
}: {
  shown: number;
  total: number;
  loading?: boolean;
  onMore: () => void;
}) {
  if (total === 0) return null;
  const done = shown >= total;

  return (
    <div className="flex items-center justify-between gap-3 border-t border-edge px-5 py-3">
      <span className="text-xs text-sub">
        Showing {shown.toLocaleString('en-IN')} of {total.toLocaleString('en-IN')}
      </span>
      {!done && (
        <button type="button" className="btn-outline !px-3 !py-1.5 !text-xs" disabled={loading} onClick={onMore}>
          {loading && <Spinner />}
          Load more
        </button>
      )}
    </div>
  );
}

/* ---------- Pagination ---------- */

export function Pagination({
  page,
  totalPages,
  onPage,
}: {
  page: number;
  totalPages: number;
  onPage: (p: number) => void;
}) {
  if (totalPages <= 1) return null;

  const pages: number[] = [];
  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
  for (let p = start; p <= Math.min(totalPages, start + 4); p++) pages.push(p);

  return (
    <div className="flex items-center justify-between gap-3 border-t border-edge px-5 py-3">
      <span className="text-xs text-sub">
        Page {page} of {totalPages}
      </span>
      <div className="flex items-center gap-1">
        <button className="btn-ghost !px-2.5" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Prev
        </button>
        {pages.map((p) => (
          <button
            key={p}
            onClick={() => onPage(p)}
            className={
              p === page
                ? 'inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gold text-sm font-semibold text-[#1a1408]'
                : 'inline-flex h-8 w-8 items-center justify-center rounded-lg text-sm text-sub transition-colors hover:bg-edge/40 hover:text-ink'
            }
          >
            {p}
          </button>
        ))}
        <button
          className="btn-ghost !px-2.5"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
